import React, { useEffect, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import './TrackingModal.css';

type TrackingView = 'milk-run' | 'transferencias-sp-pr' | 'transferencias-pe';

export interface TrackingVehicle {
  trackingKey?: string;
  viagemFinalizada?: boolean;
  placa: string;
  placaCarreta?: string;
  motorista?: string;
  sistema?: string;
  status?: string;
  estaParado?: boolean;
  poligonoAtual?: string;
  tipoPoligonoAtual?: string;
  localizacao?: string;
  latitude?: number;
  longitude?: number;
  problemaRastreador?: boolean;
  distanciaRestante?: number | string;
  origem?: string;
  destino?: string;
  enderecoOrigem?: string;
  enderecoDestino?: string;
  enderecoOrigemGeocodificado?: string;
  enderecoDestinoGeocodificado?: string;
  operacao?: string;
  numeroContrato?: string;
  numeroManifesto?: string;
  ordemColeta?: string | number;
  dataManifesto?: string;
  dataColeta?: string;
  horarioChegadaColeta?: string;
  tipoHorarioChegadaColeta?: 'realizado' | 'estimado';
  previsaoEntrega?: string;
  horarioChegadaDestino?: string;
  tipoHorarioChegadaDestino?: 'realizado' | 'estimado';
  distanciaAteDestinoKm?: number;
}

interface TrackingJourneyData {
  placa?: string;
  placaCarreta?: string | null;
  motorista?: string | null;
  empresaColeta?: string | null;
  empresaDestino?: string | null;
  enderecoColeta?: string | null;
  enderecoDestino?: string | null;
  coordenadaColeta?: { lat?: number; lng?: number; endereco?: string } | null;
  coordenadaDestino?: { lat?: number; lng?: number; endereco?: string } | null;
  dataColeta?: string | null;
  horarioChegadaColeta?: string | null;
  tipoHorarioChegadaColeta?: 'realizado' | 'estimado' | null;
  motivoHorarioChegadaColeta?: string | null;
  previsaoEntrega?: string | null;
  transitTimePrevisto?: string | null;
  transitTimeReal?: string | null;
  tipoHorarioChegadaDestino?: 'realizado' | 'estimado' | null;
  distanciaAteDestinoKm?: number | null;
  numeroContrato?: string | number | null;
  numeroManifesto?: string | number | null;
  ordemColeta?: string | number | null;
  dataManifesto?: string | null;
}

interface AutomaticTrackingJourney {
  cotacao: number | string;
  coleta?: number | string | null;
  dataColeta?: string | null;
  previsaoEntrega?: string | null;
  empresaColeta?: string | null;
  enderecoColeta?: string | null;
  empresaDestino?: string | null;
  enderecoDestino?: string | null;
  contrato?: number | string | null;
  placa?: string | null;
  placaCarreta?: string | null;
  numeroManifesto?: number | string | null;
  dataManifesto?: string | null;
  motorista?: string | null;
}

interface TrackingModalProps {
  onClose: () => void;
  vehicles: TrackingVehicle[];
}

const trackingViews: Array<{ id: TrackingView; label: string; columns: string[] }> = [
  {
    id: 'milk-run',
    label: 'Milk Run',
    columns: ['OC', 'PLACA', 'CARRETA', 'ORIGEM', 'JANELA COLETA', 'HORÁRIO DE CHEGADA', 'STATUS', 'DESTINO', 'JANELA ENTREGA', 'HORÁRIO DE CHEGADA', 'KM FALTANTE', 'LOCALIZAÇÃO', 'MOTORISTA', 'OBSERVAÇÕES'],
  },
  {
    id: 'transferencias-sp-pr',
    label: 'Transferências SP - PR',
    columns: ['OC', 'PLACA', 'CARRETA', 'MOTORISTA', 'ORIGEM', 'JANELA DE COLETA', 'HORÁRIO DE CHEGADA', 'DESTINO', 'JANELA DE ENTREGA', 'HORÁRIO DE CHEGADA', 'KM FALTANTE', 'LOCALIZAÇÃO', 'STATUS', 'OBSERVAÇÕES'],
  },
  {
    id: 'transferencias-pe',
    label: 'Transferências PE',
    columns: ['OC', 'SEMANA', 'MOTORISTA', 'EQUIPAMENTO', 'CAVALO', 'CARRETA', 'CARRETA', 'ORIGEM', 'DESTINO', 'KM', 'LOCALIZAÇÃO', 'STATUS', 'OBSERVAÇÕES'],
  },
];

const STORAGE_KEY = 'monitoramento.tracking.placasSelecionadas';
const EMAILS_STORAGE_KEY = 'monitoramento.tracking.emails';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getCurrentWeekPeriod = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  start.setDate(start.getDate() + (start.getDay() === 0 ? -6 : 1 - start.getDay()));
  const end = new Date(start);
  end.setDate(end.getDate() + 5);
  const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return { dataInicial: localDate(start), dataFinal: localDate(end) };
};

const getWeekEnd = (start: string) => {
  const [year, month, day] = start.split('-').map(Number);
  const end = new Date(Date.UTC(year, month - 1, day));
  end.setUTCDate(end.getUTCDate() + 5);
  return end.toISOString().slice(0, 10);
};

const formatPeriodDate = (date: string) => date.split('-').reverse().join('/');

const readSavedPlates = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(saved) ? saved.map(String) : [];
  } catch {
    return [];
  }
};

const valueOrDash = (value?: string | number | null) => value === undefined || value === null || value === '' ? '—' : String(value);

const BRAZIL_STATES: Record<string, string> = {
  acre: 'AC', alagoas: 'AL', amapa: 'AP', amazonas: 'AM', bahia: 'BA', ceara: 'CE',
  'distrito federal': 'DF', 'espirito santo': 'ES', goias: 'GO', maranhao: 'MA',
  'mato grosso': 'MT', 'mato grosso do sul': 'MS', 'minas gerais': 'MG', para: 'PA',
  paraiba: 'PB', parana: 'PR', pernambuco: 'PE', piaui: 'PI', 'rio de janeiro': 'RJ',
  'rio grande do norte': 'RN', 'rio grande do sul': 'RS', rondonia: 'RO', roraima: 'RR',
  'santa catarina': 'SC', 'sao paulo': 'SP', sergipe: 'SE', tocantins: 'TO',
};

const normalizeLocationPart = (value: string) => value
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const formatCityState = (location?: string) => {
  const raw = String(location || '').trim();
  if (!raw || raw === '-' || raw === '—') return '—';

  const alreadyFormatted = raw.match(/^\s*([^,]+?)\s*[-/]\s*([A-Za-z]{2})\s*$/);
  if (alreadyFormatted) return `${alreadyFormatted[1].trim().toUpperCase()} - ${alreadyFormatted[2].toUpperCase()}`;

  const parts = raw.split(',').map(part => part.trim()).filter(Boolean);
  const combinedCityState = parts.slice().reverse().map(part => part.match(/^(.+?)\s*[-/]\s*([A-Za-z]{2})$/)).find(match => {
    if (!match) return false;
    const name = normalizeLocationPart(match[1]);
    return !/^(rua|avenida|av\.?|rodovia|estrada|travessa|alameda)\b/.test(name);
  });
  if (combinedCityState) return `${combinedCityState[1].trim().toUpperCase()} - ${combinedCityState[2].toUpperCase()}`;

  let stateIndex = -1;
  let stateCode = '';

  parts.forEach((part, index) => {
    const normalized = normalizeLocationPart(part);
    const explicitCode = normalized.match(/^[a-z]{2}$/)?.[0]?.toUpperCase();
    const code = BRAZIL_STATES[normalized] || explicitCode;
    if (code && stateIndex === -1) {
      stateIndex = index;
      stateCode = code;
    }
  });

  if (stateIndex <= 0 || !stateCode) return '—';

  const city = parts.slice(0, stateIndex).reverse().find(part => {
    const normalized = normalizeLocationPart(part);
    return normalized.length > 1 &&
      !normalized.includes('regiao') &&
      !normalized.includes('microrregiao') &&
      !/^\d{5}-?\d{3}$/.test(normalized) &&
      !/^-?\d+(?:\.\d+)?$/.test(normalized);
  });

  return city ? `${city.toUpperCase()} - ${stateCode}` : '—';
};

const extractState = (...values: Array<string | undefined>) => {
  for (const value of values) {
    const raw = String(value || '').trim();
    if (!raw) continue;

    const explicitCode = raw.match(/(?:^|[,\s\-/])([A-Z]{2})(?=$|[,\s\-/]|\d{5})/i)?.[1]?.toUpperCase();
    if (explicitCode && Object.values(BRAZIL_STATES).includes(explicitCode)) return explicitCode;

    const normalizedParts = raw.split(',').map(part => normalizeLocationPart(part));
    const stateName = Object.keys(BRAZIL_STATES)
      .sort((a, b) => b.length - a.length)
      .find(name => normalizedParts.some(part => part === name || part.startsWith(`${name} - `)));
    if (stateName) return BRAZIL_STATES[stateName];
  }
  return undefined;
};

const getTripState = (address?: string, company?: string, geocodedAddress?: string) => {
  const normalizedCompany = normalizeLocationPart(company || '');
  if (normalizedCompany === 'fks logistics ltda' || normalizedCompany.includes('patio diadema')) {
    return 'SP';
  }
  if (normalizedCompany.includes('brose goi')) return 'PE';
  if (normalizedCompany.includes('brose sjp')) return 'PR';
  if (normalizedCompany.includes('brose jar')) return 'SP';
  return extractState(address, geocodedAddress, company);
};

const getWeek = (date?: string) => {
  if (!date) return '—';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '—';
  const firstDay = new Date(parsed.getFullYear(), 0, 1);
  return String(Math.ceil((((parsed.getTime() - firstDay.getTime()) / 86400000) + firstDay.getDay() + 1) / 7));
};

const getStatus = (vehicle: TrackingVehicle) => {
  if (vehicle.viagemFinalizada) return 'Finalizado';
  const normalized = String(vehicle.status || '').toLowerCase();
  const polygonType = normalizeLocationPart(vehicle.tipoPoligonoAtual || '');
  if ((vehicle.estaParado || normalized.includes('parado')) && polygonType === 'cliente' && vehicle.poligonoAtual) {
    return `No cliente (${vehicle.poligonoAtual})`;
  }
  if (normalized === 'em_movimento') return 'Em movimento';
  if (normalized === 'fora_de_rota') return 'Fora de rota';
  if (normalized === 'parado') return 'Parado';
  return valueOrDash(vehicle.status);
};

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  if (/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(',', '');
};

const formatDistance = (value?: number | string) => {
  const distance = Number(value);
  if (!Number.isFinite(distance) || distance < 0) return '—';
  return `${distance.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
};

interface TrackingCell {
  value: string | number;
  arrivalType?: 'realizado' | 'estimado';
}

const arrivalCell = (value?: string, arrivalType?: 'realizado' | 'estimado'): TrackingCell => ({
  value: valueOrDash(value),
  arrivalType,
});

const getRowValues = (view: TrackingView, vehicle: TrackingVehicle): Array<string | number | TrackingCell> => {
  const oc = vehicle.ordemColeta;
  const common = {
    oc: valueOrDash(oc),
    placa: valueOrDash(vehicle.placa),
    carreta: valueOrDash(vehicle.placaCarreta),
    motorista: valueOrDash(vehicle.motorista),
    origem: valueOrDash(vehicle.origem),
    destino: valueOrDash(vehicle.destino),
    janelaColeta: formatDateTime(vehicle.dataColeta),
    horarioChegadaColeta: arrivalCell(vehicle.horarioChegadaColeta, vehicle.tipoHorarioChegadaColeta),
    janelaEntrega: formatDateTime(vehicle.previsaoEntrega),
    horarioChegadaDestino: arrivalCell(vehicle.horarioChegadaDestino, vehicle.tipoHorarioChegadaDestino),
    km: formatDistance(vehicle.distanciaAteDestinoKm ?? vehicle.distanciaRestante),
    localizacao: formatCityState(vehicle.localizacao),
    status: getStatus(vehicle),
  };

  if (view === 'milk-run') {
    return [common.oc, common.placa, common.carreta, common.origem, common.janelaColeta, common.horarioChegadaColeta, common.status, common.destino, common.janelaEntrega, common.horarioChegadaDestino, common.km, common.localizacao, common.motorista, '—'];
  }
  if (view === 'transferencias-sp-pr') {
    return [common.oc, common.placa, common.carreta, common.motorista, common.origem, common.janelaColeta, common.horarioChegadaColeta, common.destino, common.janelaEntrega, common.horarioChegadaDestino, common.km, common.localizacao, common.status, '—'];
  }
  return [common.oc, getWeek(vehicle.dataManifesto), common.motorista, valueOrDash(vehicle.sistema), common.placa, common.carreta, '—', common.origem, common.destino, common.km, common.localizacao, common.status, '—'];
};

const getVehiclesForView = (view: TrackingView, vehicles: TrackingVehicle[]) => vehicles.filter(vehicle => {
  const originState = getTripState(vehicle.enderecoOrigem, vehicle.origem, vehicle.enderecoOrigemGeocodificado);
  const destinationState = getTripState(vehicle.enderecoDestino, vehicle.destino, vehicle.enderecoDestinoGeocodificado);
  const isSpPr = (originState === 'SP' && destinationState === 'PR') || (originState === 'PR' && destinationState === 'SP');
  const isSpPe = (originState === 'SP' && destinationState === 'PE') || (originState === 'PE' && destinationState === 'SP');

  if (view === 'transferencias-sp-pr') return isSpPr;
  if (view === 'transferencias-pe') return isSpPe;
  return !isSpPr && !isSpPe;
});

const getExcelCellValue = (cell: string | number | TrackingCell) => {
  if (typeof cell !== 'object') return cell === '—' ? '' : String(cell);
  if (cell.value === '—') return '';
  if (!cell.arrivalType) return String(cell.value);
  return `${cell.arrivalType === 'realizado' ? 'Real' : 'Previsto'} - ${cell.value}`;
};

const TrackingModal: React.FC<TrackingModalProps> = ({ onClose, vehicles }) => {
  const [activeView, setActiveView] = useState<TrackingView>('milk-run');
  const [search, setSearch] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedPlates, setSelectedPlates] = useState<string[]>(readSavedPlates);
  const [automaticJourneys, setAutomaticJourneys] = useState<AutomaticTrackingJourney[]>([]);
  const [journeysByPlate, setJourneysByPlate] = useState<Record<string, TrackingJourneyData>>({});
  const [resolvedLocations, setResolvedLocations] = useState<Record<string, string>>({});
  const locationRequestsRef = useRef<Set<string>>(new Set());
  const automaticSelectionPeriodRef = useRef('');
  const [selectedWeek, setSelectedWeek] = useState(() => getCurrentWeekPeriod().dataInicial);
  const [availableWeeks, setAvailableWeeks] = useState<Array<{ inicio: string; fim: string }>>([]);
  const [journeysLoading, setJourneysLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [emailPanelOpen, setEmailPanelOpen] = useState(false);
  const [emailInput, setEmailInput] = useState(() => window.localStorage.getItem(EMAILS_STORAGE_KEY) || '');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState('');
  const currentView = trackingViews.find(view => view.id === activeView) || trackingViews[0];
  const vehiclesByPlate = new Map(vehicles.filter(vehicle => vehicle.placa).map(vehicle => [vehicle.placa.toUpperCase(), vehicle]));
  const automaticPlates = Array.from(new Set(automaticJourneys
    .map(journey => String(journey.placa || '').trim().toUpperCase())
    .filter(plate => plate && plate !== '-')));
  const availableVehicles = Array.from(new Set([...vehiclesByPlate.keys(), ...automaticPlates])).map(plate => {
    const vehicle = vehiclesByPlate.get(plate);
    const journey = automaticJourneys.find(item => String(item.placa || '').trim().toUpperCase() === plate);
    return vehicle || {
      placa: plate,
      placaCarreta: String(journey?.placaCarreta || ''),
      motorista: String(journey?.motorista || ''),
      status: 'Sem dados de rastreamento',
      origem: String(journey?.empresaColeta || ''),
      destino: String(journey?.empresaDestino || ''),
    };
  });
  const filteredVehicles = availableVehicles.filter(vehicle => {
    const term = search.trim().toLowerCase();
    return !term || vehicle.placa.toLowerCase().includes(term) || (vehicle.motorista || '').toLowerCase().includes(term);
  });
  const selectedVehicles = selectedPlates
    .flatMap(plate => {
      const vehicle = availableVehicles.find(item => item.placa === plate);
      if (!vehicle) return [];
      const journey = journeysByPlate[plate.toUpperCase()];
      const scheduled = automaticJourneys
        .filter(item => String(item.placa || '').trim().toUpperCase() === plate.toUpperCase())
        .sort((a, b) => String(a.dataColeta || '').localeCompare(String(b.dataColeta || '')));
      if (scheduled.length > 0) return scheduled.map((schedule, scheduleIndex) => {
        const collections = new Set(String(schedule.coleta || '').split(',').map(value => value.trim()).filter(Boolean));
        const matchedJourney = journey?.ordemColeta && collections.has(String(journey.ordemColeta).trim()) ? journey : undefined;
        return {
          ...vehicle,
          trackingKey: `${plate}-${schedule.cotacao}-${schedule.coleta}`,
          viagemFinalizada: scheduleIndex < scheduled.length - 1,
          localizacao: resolvedLocations[plate.toUpperCase()] || vehicle.localizacao,
          placaCarreta: String(schedule.placaCarreta || matchedJourney?.placaCarreta || vehicle.placaCarreta || ''),
          motorista: String(schedule.motorista || matchedJourney?.motorista || vehicle.motorista || ''),
          origem: String(schedule.empresaColeta || vehicle.origem || ''),
          destino: String(schedule.empresaDestino || vehicle.destino || ''),
          enderecoOrigem: String(schedule.enderecoColeta || vehicle.enderecoOrigem || ''),
          enderecoDestino: String(schedule.enderecoDestino || vehicle.enderecoDestino || ''),
          numeroContrato: schedule.contrato ? String(schedule.contrato) : vehicle.numeroContrato,
          numeroManifesto: schedule.numeroManifesto ? String(schedule.numeroManifesto) : vehicle.numeroManifesto,
          ordemColeta: schedule.coleta ?? vehicle.ordemColeta,
          dataManifesto: schedule.dataManifesto || vehicle.dataManifesto,
          dataColeta: schedule.dataColeta || undefined,
          horarioChegadaColeta: matchedJourney?.horarioChegadaColeta || undefined,
          tipoHorarioChegadaColeta: matchedJourney?.tipoHorarioChegadaColeta || undefined,
          previsaoEntrega: schedule.previsaoEntrega || undefined,
          horarioChegadaDestino: matchedJourney?.transitTimeReal || undefined,
          tipoHorarioChegadaDestino: matchedJourney?.tipoHorarioChegadaDestino || undefined,
          distanciaAteDestinoKm: matchedJourney?.distanciaAteDestinoKm ?? (typeof vehicle.distanciaRestante === 'number' ? vehicle.distanciaRestante : undefined),
        } as TrackingVehicle;
      });
      if (!journey) return [vehicle];
      return [{
        ...vehicle,
        localizacao: resolvedLocations[plate.toUpperCase()] || vehicle.localizacao,
        placaCarreta: journey.placaCarreta || vehicle.placaCarreta,
        motorista: journey.motorista || vehicle.motorista,
        origem: journey.empresaColeta || vehicle.origem,
        destino: journey.empresaDestino || vehicle.destino,
        enderecoOrigem: journey.enderecoColeta || vehicle.enderecoOrigem,
        enderecoDestino: journey.enderecoDestino || vehicle.enderecoDestino,
        enderecoOrigemGeocodificado: journey.coordenadaColeta?.endereco || vehicle.enderecoOrigemGeocodificado,
        enderecoDestinoGeocodificado: journey.coordenadaDestino?.endereco || vehicle.enderecoDestinoGeocodificado,
        numeroContrato: journey.numeroContrato ? String(journey.numeroContrato) : vehicle.numeroContrato,
        numeroManifesto: journey.numeroManifesto ? String(journey.numeroManifesto) : vehicle.numeroManifesto,
        ordemColeta: journey.ordemColeta ?? vehicle.ordemColeta,
        dataManifesto: journey.dataManifesto || vehicle.dataManifesto,
        dataColeta: journey.dataColeta || undefined,
        horarioChegadaColeta: journey.horarioChegadaColeta || undefined,
        tipoHorarioChegadaColeta: journey.tipoHorarioChegadaColeta || undefined,
        previsaoEntrega: journey.transitTimePrevisto || journey.previsaoEntrega || undefined,
        horarioChegadaDestino: journey.transitTimeReal || undefined,
        tipoHorarioChegadaDestino: journey.tipoHorarioChegadaDestino || undefined,
        distanciaAteDestinoKm: journey.distanciaAteDestinoKm ?? undefined,
      }];
    })
    .filter((vehicle): vehicle is TrackingVehicle => Boolean(vehicle));
  const visibleVehicles = getVehiclesForView(activeView, selectedVehicles);
  const locationRequestKey = selectedPlates.map(plate => {
    const vehicle = availableVehicles.find(item => item.placa === plate);
    return `${plate}:${vehicle?.latitude ?? ''}:${vehicle?.longitude ?? ''}:${vehicle?.localizacao ?? ''}:${vehicle?.problemaRastreador ? 1 : 0}`;
  }).join('|');

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedPlates));
  }, [selectedPlates]);

  useEffect(() => {
    const controller = new AbortController();
    const period = { dataInicial: selectedWeek, dataFinal: getWeekEnd(selectedWeek) };
    const loadAutomaticJourneys = async () => {
      try {
        const params = new URLSearchParams({ ...period, _: String(Date.now()) });
        const response = await fetch(`/api/monitoramento/tv/programacao-automatica?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('Não foi possível carregar a programação semanal.');
        const payload = await response.json();
        const journeys = Array.isArray(payload?.dados) ? payload.dados as AutomaticTrackingJourney[] : [];
        setAutomaticJourneys(journeys);
        const weeks = Array.isArray(payload?.semanas) ? payload.semanas : [];
        setAvailableWeeks(current => {
          const combined = [...weeks, ...current, { inicio: period.dataInicial, fim: period.dataFinal }];
          return Array.from(new Map(combined
            .filter(week => week?.inicio && week?.fim)
            .map(week => [week.inicio, week])).values())
            .sort((a, b) => b.inicio.localeCompare(a.inicio));
        });
        if (automaticSelectionPeriodRef.current !== selectedWeek) {
          const weeklyPlates = journeys
            .map(journey => String(journey.placa || '').trim().toUpperCase())
            .filter(plate => plate && plate !== '-');
          setSelectedPlates(Array.from(new Set(weeklyPlates)));
          automaticSelectionPeriodRef.current = selectedWeek;
        }
      } catch (error: any) {
        if (error?.name !== 'AbortError') console.error('[Tracking] Erro ao carregar programação automática:', error);
      }
    };

    loadAutomaticJourneys();
    const interval = window.setInterval(loadAutomaticJourneys, 60 * 1000);
    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, [selectedWeek]);

  useEffect(() => {
    const controller = new AbortController();
    const vehiclesToResolve = selectedPlates
      .map(plate => availableVehicles.find(vehicle => vehicle.placa === plate))
      .filter((vehicle): vehicle is TrackingVehicle => Boolean(vehicle))
      .filter(vehicle => {
        const requestKey = `${vehicle.placa.toUpperCase()}:${Number(vehicle.latitude).toFixed(4)},${Number(vehicle.longitude).toFixed(4)}`;
        return !vehicle.problemaRastreador &&
          Number.isFinite(Number(vehicle.latitude)) &&
          Number.isFinite(Number(vehicle.longitude)) &&
          !locationRequestsRef.current.has(requestKey);
      });

    const resolveLocations = async () => {
      for (const vehicle of vehiclesToResolve) {
        if (controller.signal.aborted) break;
        const requestKey = `${vehicle.placa.toUpperCase()}:${Number(vehicle.latitude).toFixed(4)},${Number(vehicle.longitude).toFixed(4)}`;
        locationRequestsRef.current.add(requestKey);
        try {
          const params = new URLSearchParams({
            lat: String(vehicle.latitude),
            lng: String(vehicle.longitude),
          });
          const response = await fetch(`/api/monitoramento/reverse-geocode?${params.toString()}`, {
            signal: controller.signal,
          });
          if (!response.ok) {
            locationRequestsRef.current.delete(requestKey);
            continue;
          }
          const data = await response.json();
          const address = data?.address || {};
          const city = address.city || address.town || address.municipality || address.village || address.county;
          const isoState = address['ISO3166-2-lvl4'] || address['ISO3166-2-lvl6'] || '';
          const stateFromIso = String(isoState).match(/BR-([A-Z]{2})/i)?.[1]?.toUpperCase();
          const stateFromName = BRAZIL_STATES[normalizeLocationPart(address.state || '')];
          const state = stateFromIso || stateFromName;
          const resolvedLocation = city && state
            ? `${String(city).toUpperCase()} - ${state}`
            : (data?.display_name || data?.endereco || '');

          if (resolvedLocation) {
            setResolvedLocations(current => ({
              ...current,
              [vehicle.placa.toUpperCase()]: resolvedLocation,
            }));
          }
        } catch (error: any) {
          locationRequestsRef.current.delete(requestKey);
          if (error?.name === 'AbortError') break;
          console.warn(`[Tracking] Não foi possível resolver a localização de ${vehicle.placa}.`);
        }
      }
    };

    resolveLocations();
    return () => controller.abort();
  }, [locationRequestKey]);

  useEffect(() => {
    if (selectedPlates.length === 0) {
      setJourneysByPlate({});
      setJourneysLoading(false);
      return;
    }

    const controller = new AbortController();
    const plateBatches: string[][] = [];
    for (let index = 0; index < selectedPlates.length; index += 30) {
      plateBatches.push(selectedPlates.slice(index, index + 30));
    }

    setJourneysLoading(true);
    Promise.all(plateBatches.map(async batch => {
      const response = await fetch(`/api/monitoramento/tv/dados-viagem?placas=${encodeURIComponent(batch.join(','))}`, {
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Não foi possível carregar os dados de viagem do Tracking.');
      return response.json() as Promise<Record<string, TrackingJourneyData>>;
    }))
      .then(results => {
        if (!controller.signal.aborted) setJourneysByPlate(Object.assign({}, ...results));
      })
      .catch(error => {
        if (error?.name !== 'AbortError') {
          console.error('[Tracking] Erro ao carregar dados de viagem:', error);
          setJourneysByPlate({});
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setJourneysLoading(false);
      });

    return () => controller.abort();
  }, [selectedPlates]);

  const togglePlate = (plate: string) => {
    setSelectedPlates(current => current.includes(plate) ? current.filter(item => item !== plate) : [...current, plate]);
  };

  const exportTracking = async (sendByEmail = false) => {
    if (selectedVehicles.length === 0 || journeysLoading || exporting || sendingEmail) return;

    const recipients = emailInput
      .split(/[;,\s]+/)
      .map(email => email.trim().toLowerCase())
      .filter(Boolean);
    const uniqueRecipients = Array.from(new Set(recipients));
    if (sendByEmail && (uniqueRecipients.length === 0 || uniqueRecipients.some(email => !EMAIL_PATTERN.test(email)))) {
      setEmailFeedback('Informe ao menos um e-mail válido. Separe vários endereços por vírgula.');
      return;
    }

    if (sendByEmail) {
      setSendingEmail(true);
      setEmailFeedback('');
    } else {
      setExporting(true);
    }
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Sistema FKS';
      workbook.created = new Date();

      const worksheetNames: Record<TrackingView, string> = {
        'milk-run': 'Milk Run',
        'transferencias-sp-pr': 'Transferências SP-PR',
        'transferencias-pe': 'Transferências PE',
      };

      trackingViews.forEach(view => {
        const worksheet = workbook.addWorksheet(worksheetNames[view.id], {
          views: [{ state: 'frozen', ySplit: 1, showGridLines: false }],
        });
        const viewVehicles = getVehiclesForView(view.id, selectedVehicles);

        worksheet.addRow(view.columns);
        viewVehicles.forEach(vehicle => {
          worksheet.addRow(getRowValues(view.id, vehicle).map(getExcelCellValue));
        });

        const header = worksheet.getRow(1);
        header.height = 28;
        header.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
        header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A5F' } };
        header.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        header.eachCell(cell => {
          cell.border = { bottom: { style: 'medium', color: { argb: 'FF1565C0' } } };
        });

        worksheet.autoFilter = {
          from: { row: 1, column: 1 },
          to: { row: Math.max(1, worksheet.rowCount), column: view.columns.length },
        };

        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          row.height = 24;
          row.alignment = { vertical: 'middle', wrapText: true };
          if (rowNumber % 2 === 0) {
            row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
          }
          row.eachCell(cell => {
            cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF334155' } };
            cell.border = { bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } } };
          });
        });

        worksheet.columns.forEach((column, columnIndex) => {
          let contentLength = view.columns[columnIndex]?.length || 10;
          column.eachCell?.({ includeEmpty: false }, cell => {
            contentLength = Math.max(contentLength, String(cell.value ?? '').length);
          });
          column.width = Math.min(Math.max(contentLength + 2, 12), 34);
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const now = new Date();
      const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
      ].join('-');
      const fileName = `Tracking-${timestamp}.xlsx`;
      if (sendByEmail) {
        const bytes = new Uint8Array(buffer as ArrayBuffer);
        let binary = '';
        const chunkSize = 0x8000;
        for (let index = 0; index < bytes.length; index += chunkSize) {
          binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
        }
        const response = await fetch('/api/monitoramento/tracking/enviar-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(window.localStorage.getItem('token')
              ? { Authorization: `Bearer ${window.localStorage.getItem('token')}` }
              : {}),
          },
          body: JSON.stringify({
            destinatarios: uniqueRecipients,
            nomeArquivo: fileName,
            arquivoBase64: window.btoa(binary),
          }),
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result?.error || 'Não foi possível enviar o e-mail.');
        window.localStorage.setItem(EMAILS_STORAGE_KEY, uniqueRecipients.join(', '));
        setEmailInput(uniqueRecipients.join(', '));
        setEmailFeedback(`Arquivo enviado para ${uniqueRecipients.length} destinatário${uniqueRecipients.length === 1 ? '' : 's'}.`);
      } else {
        saveAs(
          new Blob([buffer as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
          fileName,
        );
      }
    } catch (error) {
      console.error('[Tracking] Erro ao exportar o arquivo Excel:', error);
      if (sendByEmail) {
        setEmailFeedback(error instanceof Error ? error.message : 'Não foi possível enviar o arquivo por e-mail.');
      } else {
        window.alert('Não foi possível exportar o Tracking para Excel. Tente novamente.');
      }
    } finally {
      if (sendByEmail) setSendingEmail(false);
      else setExporting(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="tracking-overlay" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="tracking-modal" role="dialog" aria-modal="true" aria-labelledby="tracking-title">
        <header className="tracking-header">
          <div className="tracking-heading">
            <span className="tracking-heading-icon" aria-hidden="true">⌖</span>
            <div>
              <h2 id="tracking-title">Tracking</h2>
              <p>Acompanhamento das operações de transporte</p>
            </div>
          </div>
          <button className="tracking-close" type="button" onClick={onClose} aria-label="Fechar Tracking">×</button>
        </header>

        <div className="tracking-tabs" role="tablist" aria-label="Visões de Tracking">
          {trackingViews.map(view => (
            <button
              key={view.id}
              id={`tracking-tab-${view.id}`}
              className={`tracking-tab ${activeView === view.id ? 'active' : ''}`}
              type="button"
              role="tab"
              aria-selected={activeView === view.id}
              aria-controls="tracking-table-panel"
              onClick={() => setActiveView(view.id)}
            >
              {view.label}
            </button>
          ))}
        </div>

        <div className="tracking-filters">
          <div className="tracking-filter-primary">
          <label className="tracking-week-filter">
            <span>Filtrar por semana</span>
            <select value={selectedWeek} onChange={event => setSelectedWeek(event.target.value)}>
              {availableWeeks.map(week => (
                <option key={week.inicio} value={week.inicio}>
                  {formatPeriodDate(week.inicio)} até {formatPeriodDate(week.fim)}
                </option>
              ))}
            </select>
          </label>
          <div className="tracking-selector">
            <button className="tracking-selector-trigger" type="button" onClick={() => setSelectorOpen(open => !open)} aria-expanded={selectorOpen}>
              <span>Selecionar placas</span>
              <strong>{selectedPlates.length}</strong>
              <span aria-hidden="true">{selectorOpen ? '▴' : '▾'}</span>
            </button>
            {selectorOpen && (
              <div className="tracking-selector-menu">
                <input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar placa ou motorista..." autoFocus />
                <div className="tracking-selector-actions">
                  <button type="button" onClick={() => setSelectedPlates(availableVehicles.map(vehicle => vehicle.placa))}>Selecionar todas</button>
                  <button type="button" onClick={() => setSelectedPlates([])}>Limpar</button>
                </div>
                <div className="tracking-options">
                  {filteredVehicles.length === 0 ? <p>Nenhuma placa encontrada.</p> : filteredVehicles.map(vehicle => (
                    <label key={vehicle.placa} className="tracking-option">
                      <input type="checkbox" checked={selectedPlates.includes(vehicle.placa)} onChange={() => togglePlate(vehicle.placa)} />
                      <span><strong>{vehicle.placa}</strong><small>{vehicle.motorista || vehicle.operacao || 'Sem motorista informado'}</small></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
          <div className="tracking-filter-info">
            <div className="tracking-arrival-legend" aria-label="Legenda dos horários de chegada">
              <span><b className="tracking-arrival-icon estimated">⏱</b> Previsto</span>
              <span><b className="tracking-arrival-icon actual">✓</b> Real</span>
            </div>
            <button
              className="tracking-export-button"
              type="button"
              onClick={() => exportTracking(false)}
              disabled={selectedVehicles.length === 0 || journeysLoading || exporting || sendingEmail}
              title={selectedVehicles.length === 0 ? 'Selecione ao menos uma placa para exportar' : 'Exportar as três visões em um arquivo Excel'}
            >
              <span aria-hidden="true">⇩</span>
              {exporting ? 'Exportando...' : 'Exportar Excel'}
            </button>
            <button
              className="tracking-email-button"
              type="button"
              onClick={() => { setEmailPanelOpen(open => !open); setEmailFeedback(''); }}
              disabled={selectedVehicles.length === 0 || journeysLoading || exporting || sendingEmail}
              title="Enviar o Excel do Tracking por e-mail"
            >
              <span aria-hidden="true">✉</span>
              Enviar por e-mail
            </button>
            <span className="tracking-filter-summary">{journeysLoading ? 'Atualizando dados de viagem...' : `${visibleVehicles.length} viagem${visibleVehicles.length === 1 ? '' : 'ns'} nesta visão`}</span>
          </div>
        </div>

        {emailPanelOpen && (
          <div className="tracking-email-panel">
            <div>
              <label htmlFor="tracking-email-input">Destinatários</label>
              <span>Separe vários e-mails por vírgula, ponto e vírgula ou espaço.</span>
            </div>
            <textarea
              id="tracking-email-input"
              value={emailInput}
              onChange={event => { setEmailInput(event.target.value); setEmailFeedback(''); }}
              placeholder="exemplo@fkslogistics.com.br"
              rows={2}
              autoFocus
            />
            <button type="button" onClick={() => exportTracking(true)} disabled={sendingEmail}>
              {sendingEmail ? 'Enviando...' : 'Enviar arquivo'}
            </button>
            {emailFeedback && <p className={emailFeedback.startsWith('Arquivo enviado') ? 'success' : 'error'}>{emailFeedback}</p>}
          </div>
        )}

        <div id="tracking-table-panel" className="tracking-panel" role="tabpanel" aria-labelledby={`tracking-tab-${activeView}`}>
          <div className="tracking-table-scroll">
            <table className="tracking-table">
              <thead><tr>{currentView.columns.map((column, index) => <th key={`${column}-${index}`} scope="col">{column}</th>)}</tr></thead>
              <tbody>
                {visibleVehicles.length === 0 ? <tr className="tracking-empty-row">
                  <td colSpan={currentView.columns.length}>
                    <div className="tracking-empty-state">
                      <span className="tracking-empty-icon" aria-hidden="true">⌖</span>
                      <strong>{selectedVehicles.length === 0 ? 'Selecione as placas que deseja acompanhar' : 'Nenhuma viagem nesta classificação'}</strong>
                      <span>{selectedVehicles.length === 0 ? `Use o filtro acima para incluir veículos em ${currentView.label}.` : `As placas selecionadas não possuem viagens classificadas como ${currentView.label}.`}</span>
                    </div>
                  </td>
                </tr> : visibleVehicles.map(vehicle => (
                  <tr key={`${activeView}-${vehicle.trackingKey || vehicle.placa}`}>
                    {getRowValues(activeView, vehicle).map((cell, index) => {
                      const isTrackingCell = typeof cell === 'object';
                      const value = isTrackingCell ? cell.value : cell;
                      const arrivalType = isTrackingCell ? cell.arrivalType : undefined;
                      return (
                        <td key={`${vehicle.placa}-${index}`} className={currentView.columns[index] === 'STATUS' ? 'tracking-status-cell' : ''}>
                          {arrivalType && value !== '—' && (
                            <span
                              className={`tracking-arrival-icon ${arrivalType === 'realizado' ? 'actual' : 'estimated'}`}
                              title={arrivalType === 'realizado' ? 'Horário real registrado no polígono' : 'Horário previsto pela localização e velocidade atuais'}
                              aria-label={arrivalType === 'realizado' ? 'Horário real' : 'Horário previsto'}
                            >
                              {arrivalType === 'realizado' ? '✓' : '⏱'}
                            </span>
                          )}
                          {value}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrackingModal;
